import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import { Formik } from 'formik';
import Image from 'next/image';

import { ISanitizedCompany } from 'types/company';
import { IUserResponse } from 'types/user';

import { gradient } from 'constants/theme';

import { Drawer } from 'components/shared/Drawer';
import { Spinner } from 'components/shared/Spinner';
import { TextField } from 'components/shared/forms/TextField';

import fallbackProfilePic from 'public/authentication/avatar.png';

import { useDrawer } from './ProfileDrawer.hooks';

interface IProfileDrawerHandle {
  openDrawer: () => void;
  closeDrawer: () => void;
}

interface IProps {
  user: IUserResponse;
  company: ISanitizedCompany;
}

const InternalProfileDrawer: ForwardRefRenderFunction<
  IProfileDrawerHandle,
  IProps
> = ({ user, company }, ref) => {
  const publishDrawerRef = useRef<ElementRef<typeof Drawer>>(null);

  const { handleOpen, handleClose, handleSubmit } = useDrawer({
    publishDrawerRef,
    user,
    company,
  });

  useImperativeHandle(ref, () => ({
    openDrawer: handleOpen,
    closeDrawer: handleClose,
  }));

  return (
    <Drawer ref={publishDrawerRef}>
      <header className="p-6 sm:px-12 sm:pb-5 sm:pt-7">
        <div className="flex flex-row justify-between space-x-2 text-xl">
          <h1 className="text-2xl font-bold">My Profile</h1>

          <div className="flex w-20 items-center justify-end">
            <button className="text-3xl" onClick={handleClose}>
              &times;
            </button>
          </div>
        </div>
      </header>

      <div className="flex-grow">
        <div className="border-b border-t border-b-black/10 border-t-black/10 p-6 dark:border-b-white/10 dark:border-t-white/10 sm:px-12 sm:pb-7 sm:pt-6">
          <h3 className="mb-6 text-lg font-bold">Profile information</h3>

          <Formik
            initialValues={{
              ...user.profile,
              avatar: user.profile.avatar || undefined,
              avatarPreview: undefined,
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, handleSubmit, values }) => {
              const handleImageChange = (
                event: React.ChangeEvent<HTMLInputElement>,
              ) => {
                const file = event.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                  setFieldValue('avatar', file);
                  setFieldValue('avatarPreview', URL.createObjectURL(file));
                }
              };

              return (
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <label className="group relative block h-32 w-32 cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />

                      <Image
                        src={
                          values.avatarPreview ||
                          values.avatar ||
                          fallbackProfilePic
                        }
                        alt="Profile"
                        width={128}
                        height={128}
                        className="h-full w-full rounded-full object-cover"
                      />

                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                        <span className="text-2xl">+</span>
                      </div>
                    </label>

                    <div className="flex space-x-4">
                      <TextField
                        autoFocus
                        required
                        name="firstName"
                        type="text"
                        className="w-full placeholder:text-sm"
                        placeholder="First name"
                      />

                      <TextField
                        name="lastName"
                        type="text"
                        className="w-full placeholder:text-sm"
                        placeholder="Last name"
                      />
                    </div>

                    <TextField
                      name="job"
                      type="text"
                      className="w-full placeholder:text-sm"
                      placeholder="Job"
                    />

                    <TextField
                      name="phone"
                      type="text"
                      className="w-full placeholder:text-sm"
                      placeholder="Phone number (+491701234567)"
                    />
                  </div>

                  <div className="mt-6 flex justify-end space-x-4">
                    <button type="button" onClick={handleClose}>
                      Cancel
                    </button>

                    <button
                      className="flex rounded-lg px-6 py-2 text-white shadow-md dark:text-mineShaftGrey"
                      style={{ backgroundImage: gradient }}
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Save
                      {isSubmitting && (
                        <span className="ml-3">
                          <Spinner className="text-white" size={24} />
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Drawer>
  );
};

InternalProfileDrawer.displayName = 'ProfileDrawer';

export const ProfileDrawer = forwardRef(InternalProfileDrawer);
